/**
 * "Näytä lisää" clamping for any list of events.
 *
 * A container opts in by carrying data-initial-count; everything past that many
 * VISIBLE rows is hidden until the container is expanded, and one click reveals
 * the rest in full — there is no second page. Two lists use it:
 *
 *  - the calendar panel in #kalenteri, where opening a busy month would
 *    otherwise drop twenty rows between the ribbon and the page below it
 *  - each month group in the agenda list
 *
 * Three contracts hold this together:
 *
 *  - Rows are found as [data-cat], the same marker the category filter uses.
 *    Scoped to the container, that is exactly the event rows in both blocks.
 *
 *  - Clamping counts SURVIVORS, not raw children. The filter and the clamp both
 *    decide visibility, so event-filters.js calls applyClamp() after every
 *    filter pass. Counting raw children would let a filter clamp away the only
 *    survivors, or reveal rows the filter had hidden. Runs filters -> clamp.
 *
 *  - The click handler is delegated from document, because the calendar rebuilds
 *    its panel with innerHTML on every month change. A handler bound per button
 *    at init would be discarded with the markup it was attached to.
 *
 * Nothing is clamped server-side: without JS the full list renders and the
 * button stays hidden, rather than locking rows behind a control that cannot
 * work.
 */

const GROUP = '[data-initial-count]';
const ITEM = '[data-cat]';
const BTN = '[data-ev-more]';
const HIDDEN_BY_FILTER = 'is-filtered-out';

/** Re-clamp one container against whatever the filter has left visible. */
function clampGroup(group) {
	const limit = Number.parseInt(group.dataset.initialCount, 10);
	if (!Number.isFinite(limit) || limit < 1) return;

	const expanded = group.classList.contains('is-expanded');
	const all = [...group.querySelectorAll(ITEM)];
	const visible = all.filter((item) => !item.classList.contains(HIDDEN_BY_FILTER));

	all.forEach((item) => item.classList.remove('is-clamped'));
	if (!expanded) {
		visible.slice(limit).forEach((item) => item.classList.add('is-clamped'));
	}

	const remaining = Math.max(0, visible.length - limit);
	const btn = group.querySelector(BTN);
	if (!btn) return;

	btn.hidden = expanded || remaining === 0;
	const label = btn.dataset.labelMore || 'Näytä lisää';
	btn.textContent = `${label} (${remaining})`;
}

/**
 * Re-apply clamping across a scope. Safe to call repeatedly — it recomputes
 * from scratch rather than toggling, so it cannot drift out of sync.
 */
export function applyClamp(scope = document) {
	scope.querySelectorAll(GROUP).forEach(clampGroup);
	// querySelectorAll only looks at descendants, so a scope that is itself a
	// container would be skipped.
	if (scope.matches?.(GROUP)) clampGroup(scope);
}

export function initEventClamp() {
	// Delegated: survives the calendar replacing its panel markup.
	document.addEventListener('click', (event) => {
		const btn = event.target.closest?.(BTN);
		if (!btn) return;

		const group = btn.closest(GROUP);
		if (!group) return;

		const limit = Number.parseInt(group.dataset.initialCount, 10) || 0;
		const visible = [...group.querySelectorAll(ITEM)].filter(
			(item) => !item.classList.contains(HIDDEN_BY_FILTER)
		);

		group.classList.add('is-expanded');
		clampGroup(group);

		// Focus the first newly revealed row, so keyboard and screen reader users
		// land on the new content instead of a button that just vanished.
		visible[limit]?.focus({ preventScroll: true });
	});

	// The calendar renders a fresh panel for each month opened.
	document.addEventListener('kpop:calendar-rendered', (event) => {
		applyClamp(event.target);
		applyClamp();
	});

	applyClamp();
}
